import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { 
  ArrowBack, 
  Share, 
  OpenInNew,
  Person,
  Description,
  ListAlt,
  Warning,
  Link as LinkIcon,
  ExpandMore,
  ExpandLess,
  CheckCircle
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  getLocalizedName, 
  getLocalizedWhoCanApply,
  hasGroupedDocuments,
  getGroupedDocuments,
  getLocalizedRequiredLinks
} from '../models/FormItem';

export const FormDetailPage: React.FC = () => {
  const { categoryId, formId } = useParams<{ categoryId?: string; formId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [expandedSections, setExpandedSections] = useState<string[]>(['whoCanApply', 'documents', 'steps', 'requiredLinks']);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; url: string; label: string }>({ open: false, url: '', label: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const { data: form, isLoading, error } = useForm(formId || '');

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleShare = async () => {
    if (navigator.share && form) {
      try {
        await navigator.share({
          title: getLocalizedName(form, currentLanguage),
          text: t('app_title'),
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share failed:', err);
      }
    }
  };

  const handleLinkShare = async (label: string, url: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: label,
          text: label,
          url: url,
        });
      } catch (err) {
        console.log('Share failed:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${label}\n${url}`);
    }
  };

  const handleOpenLink = (url: string, label: string) => {
    setConfirmDialog({ open: true, url, label });
  };

  const handleConfirmOpen = () => {
    const { url } = confirmDialog;
    setConfirmDialog({ open: false, url: '', label: '' });
    
    try {
      // Ensure URL has proper scheme
      let finalUrl = url.trim();
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`;
      }
      
      // Open the link
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
      
      // Show success notification
      setSnackbar({
        open: true,
        message: 'Opening external browser...',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error opening URL:', error);
      setSnackbar({
        open: true,
        message: 'Could not open link',
        severity: 'error'
      });
    }
  };

  const handleCancelOpen = () => {
    setConfirmDialog({ open: false, url: '', label: '' });
  };

  // Get common mistakes
  const getCommonMistakes = (): string[] => {
    const raw = form?._rawCommonMistakes;
    if (!raw) return form?.commonMistakes || [];
    
    const langData = raw[currentLanguage] || raw;
    if (Array.isArray(langData)) {
      return langData;
    }
    return form?.commonMistakes || [];
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !form) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('error_occurred')}</Alert>
      </Container>
    );
  }

  const isGrouped = hasGroupedDocuments(form, currentLanguage);
  const groupedDocs = isGrouped ? getGroupedDocuments(form, currentLanguage) : [];

  // Get flat documents for non-grouped mode
  const getFlatDocuments = (): string[] => {
    if (isGrouped) return [];
    const raw = form._rawRequiredDocuments;
    if (!raw) return form.requiredDocuments || [];
    
    const langData = raw[currentLanguage] || raw;
    if (Array.isArray(langData) && typeof langData[0] === 'string') {
      return langData;
    }
    return form.requiredDocuments || [];
  };

  const flatDocuments = getFlatDocuments();

  // Get steps
  const getSteps = (): string[] => {
    const raw = form._rawSteps;
    if (!raw) return form.steps || [];
    
    const langData = raw[currentLanguage] || raw;
    if (Array.isArray(langData)) {
      return langData;
    }
    return form.steps || [];
  };

  const steps = getSteps();
  const commonMistakes = getCommonMistakes();
  const requiredLinks = getLocalizedRequiredLinks(form, currentLanguage);

  const LinkCard: React.FC<{
    label: string;
    url: string;
    isPortal?: boolean;
  }> = ({ label, url, isPortal = false }) => {
    const primaryColor = isPortal ? '#FF9800' : '#1976D2';
    const bgColor = isPortal ? '#FFF3E0' : '#E3F2FD';
    const borderColor = isPortal ? '#FFB74D' : '#64B5F6';

    return (
      <Paper
        sx={{
          mb: 1.5,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1.5px solid ${borderColor}`,
          background: isPortal
            ? `linear-gradient(135deg, ${bgColor} 0%, #FFE0B2 100%)`
            : bgColor,
        }}
      >
        {/* Main Link Area */}
        <Box
          onClick={() => handleOpenLink(url, label)}
          sx={{
            p: isPortal ? 2 : 1.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.02)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isPortal ? (
              <Avatar
                sx={{
                  bgcolor: primaryColor,
                  mr: 1.5,
                  width: 40,
                  height: 40,
                }}
              >
                <LinkIcon />
              </Avatar>
            ) : (
              <LinkIcon sx={{ color: primaryColor, mr: 1.5, fontSize: 24 }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant={isPortal ? 'subtitle1' : 'body1'}
                fontWeight="bold"
                sx={{ color: primaryColor }}
              >
                {label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: primaryColor,
                  opacity: 0.8,
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              >
                {url}
              </Typography>
            </Box>
            <OpenInNew
              sx={{
                color: primaryColor,
                fontSize: isPortal ? 20 : 18,
                ml: 1,
              }}
            />
          </Box>
        </Box>

        {/* Share Button */}
        <Box
          sx={{
            borderTop: `1px solid ${borderColor}`,
          }}
        >
          <Box
            onClick={() => handleLinkShare(label, url)}
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <Share sx={{ fontSize: 16, color: primaryColor, mr: 1 }} />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: primaryColor, fontSize: 13 }}
            >
              {t('share')}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  const ExpandableSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    sectionKey: string;
    children: React.ReactNode;
  }> = ({ title, icon, sectionKey, children }) => {
    const isExpanded = expandedSections.includes(sectionKey);
    
    return (
      <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          onClick={() => toggleSection(sectionKey)}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.02)'
            }
          }}
        >
          <Avatar sx={{ bgcolor: '#EDE7F6', color: '#673AB7', mr: 2 }}>
            {icon}
          </Avatar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, pt: 0 }}>
            {children}
          </Box>
        </Collapse>
      </Paper>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' } }}>
          {getLocalizedName(form, currentLanguage)}
        </Typography>
      </Box>

      {/* Title Card */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#F3E5F5', borderRadius: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' } }}>
          {getLocalizedName(form, currentLanguage)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getLocalizedWhoCanApply(form, currentLanguage)?.substring(0, 100)}...
        </Typography>
      </Paper>

      {/* Who Can Apply Section */}
      <ExpandableSection
        title={t('who_can_apply')}
        icon={<Person />}
        sectionKey="whoCanApply"
      >
        <Box sx={{ bgcolor: '#E3F2FD', p: 2, borderRadius: 2 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            {getLocalizedWhoCanApply(form, currentLanguage)}
          </Typography>
        </Box>
      </ExpandableSection>

      {/* Required Documents Section */}
      <ExpandableSection
        title={t('required_documents')}
        icon={<Description />}
        sectionKey="documents"
      >
        {isGrouped ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groupedDocs.map((group, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: '#F5F5F5' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ color: '#4CAF50', mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {group.type}
                  </Typography>
                </Box>
                <List dense>
                  {group.documents.map((doc, docIndex) => (
                    <ListItem key={docIndex} sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`• ${doc}`}
                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ))}
          </Box>
        ) : (
          <List>
            {flatDocuments.map((doc, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <CheckCircle sx={{ color: '#4CAF50', mr: 2, fontSize: 20 }} />
                <ListItemText 
                  primary={doc}
                  primaryTypographyProps={{ fontSize: '0.95rem' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </ExpandableSection>

      {/* Steps Section */}
      <ExpandableSection
        title={t('steps')}
        icon={<ListAlt />}
        sectionKey="steps"
      >
        <Box>
          {steps.map((step, index) => (
            <Box key={index} sx={{ display: 'flex', mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: '#673AB7',
                    width: 40,
                    height: 40,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {index + 1}
                </Avatar>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      flexGrow: 1,
                      bgcolor: '#E0E0E0',
                      minHeight: 40,
                      mt: 1
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, pt: 1 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                  {step}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </ExpandableSection>

      {/* Common Mistakes Section */}
      {commonMistakes.length > 0 && (
        <ExpandableSection
          title={t('common_mistakes')}
          icon={<Warning />}
          sectionKey="mistakes"
        >
          <List>
            {commonMistakes.map((mistake, index) => (
              <ListItem key={index} sx={{ py: 1, alignItems: 'flex-start' }}>
                <Typography sx={{ mr: 1, color: '#F57C00' }}>•</Typography>
                <ListItemText 
                  primary={mistake}
                  primaryTypographyProps={{ fontSize: '0.95rem' }}
                />
              </ListItem>
            ))}
          </List>
        </ExpandableSection>
      )}

      {/* Required Links Section */}
      <ExpandableSection
        title={t('required_links')}
        icon={<LinkIcon />}
        sectionKey="requiredLinks"
      >
        <Box>
          {/* Main Portal Link (always shown first if exists) */}
          {form.portalLink && (
            <LinkCard
              label={t('official_portal')}
              url={form.portalLink}
              isPortal={true}
            />
          )}

          {/* Additional Links */}
          {requiredLinks.map((link, index) => (
            <LinkCard
              key={index}
              label={link.label}
              url={link.url}
              isPortal={false}
            />
          ))}
        </Box>
      </ExpandableSection>

      {/* Document Checklist Button */}
      <Button
        variant="outlined"
        fullWidth
        startIcon={<CheckCircle />}
        onClick={() => navigate(categoryId ? `/document/${categoryId}/${formId}/checklist` : `/form/${formId}/checklist`)}
        sx={{
          mt: 3,
          py: 1.5,
          borderColor: '#673AB7',
          color: '#673AB7',
          borderWidth: 2,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#5E35B1',
            borderWidth: 2,
            bgcolor: 'rgba(103,58,183,0.04)'
          }
        }}
      >
        {t('document_checklist')}
      </Button>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelOpen}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <OpenInNew sx={{ color: '#1976D2', mr: 1.5 }} />
            <Typography variant="h6" component="span">
              Open External Link
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You will be redirected to an external website:
          </Typography>
          <Paper
            sx={{
              p: 1.5,
              bgcolor: '#F5F5F5',
              borderRadius: 1,
              mb: 2
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#1976D2',
                wordBreak: 'break-all',
                fontSize: '0.875rem'
              }}
            >
              {confirmDialog.url}
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelOpen}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOpen}
            variant="contained"
            sx={{
              bgcolor: '#673AB7',
              '&:hover': { bgcolor: '#5E35B1' },
              textTransform: 'none'
            }}
          >
            Open Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
